// src/components/quotes/quoteBoard.js
//
// The quote kanban's rules — which column a quote sits in, and the four
// fulfilment steps Craig ticks after a client accepts.
//
// ⛔ FULFILMENT IS A SECOND AXIS, NOT FOUR MORE STATUSES. Craig's words were
// "add a status invoiced, a status paid…", but the requirement that followed —
// "I need to be able to select each status individually, as I may order the
// product before they pay" — is the proof they cannot be statuses. A status
// holds ONE value; these hold FOUR independent values at once. `q.status`
// keeps saying where the quote stands with the client (draft · sent · won ·
// lost); `q.fulfilment` says how far Craig has got with the paperwork. The
// same ruling already exists for the roadmap in
// reports/quoteState.js ("never collapse them into one label"). Never add
// invoiced / paid / ordered / autotask to the status enum.
//
// ⚠ TIMESTAMPS, NOT BOOLEANS. null = not done, a number = done at that moment.
// `!!f.invoiced` reads exactly like a boolean, and it also answers "when did I
// invoice this?" — which matters because the stated next step is automating
// these ticks (QBO for invoiced/paid, the Autotask push for autotask), and an
// automated tick wants to record when it fired. Nothing is wired to do that
// yet; that is a decision, not a wiring job (a quote pushed to Autotask before
// the client accepted would tick a step on a quote that is not won).
//
// ⛔ NONE OF THIS REACHES THE CLIENT. Fulfilment is internal bookkeeping, the
// same class as cost and margin. quoteRenderer.js must never read it.
//
// React-free so it can be unit-tested directly — the same reason quoteRecord,
// quoteTerms and quoteState are.

export const FULFILMENT_STEPS = ["invoiced", "paid", "ordered", "autotask"];

/* draft · sent · won · lost. "Complete" is a COLUMN, derived from all four
   steps being ticked — Craig's call (2026-09-22): no `completed` status. A
   `completed` status shipped for about an hour in 55e2909 before that call;
   any quote it touched is treated as won below so it lands in its fulfilment
   column rather than falling to Draft. */
export const QUOTE_STATUSES = ["draft", "sent", "won", "lost"];
const LEGACY_COMPLETED = "completed";

export const BOARD_COLUMNS = ["draft", "sent", "won", ...FULFILMENT_STEPS, "complete"];

export const COLUMN_LABELS = {
  draft: "Draft", sent: "Sent", won: "Won",
  invoiced: "Invoiced", paid: "Paid", ordered: "Ordered", autotask: "Autotask",
  complete: "Complete",
};

export const STEP_LABELS = { invoiced: "Invoiced", paid: "Paid", ordered: "Ordered", autotask: "Autotask" };

/* Only the four fulfilment columns accept a drop. Draft → Sent has a real
   email send behind it (sentAt is stamped by the send), so a stray drag must
   never imply a quote went to a client; Won is a client decision made on the
   builder's status pills; Complete is DERIVED, reached by ticking the fourth
   step, and accepting a drop there would mean silently setting up to four
   flags at once. */
export const DROP_COLUMNS = new Set(FULFILMENT_STEPS);
export const isDropColumn = (col) => DROP_COLUMNS.has(col);

export function emptyFulfilment() {
  return { invoiced: null, paid: null, ordered: null, autotask: null };
}

const hasAllSteps = (f) =>
  !!f && typeof f === "object" && FULFILMENT_STEPS.every((s) => Object.prototype.hasOwnProperty.call(f, s));

/**
 * The quote with a `fulfilment` field, backfilled if absent.
 *
 * ⚠ RETURNS THE SAME OBJECT REFERENCE when the field is already present, and
 * that is load-bearing: ensureMigrated runs on every read, and a fresh
 * reference each time would hand React a changed quote on every render and
 * make the debounced save fire forever. withQuoteTerms has the same contract
 * for the same reason.
 *
 * ⛔ APPLIED TO THE RESULT OF THE SHAPE MIGRATION, NEVER INSIDE IT.
 * shapeMigrateQuote early-returns on `Array.isArray(q.sections)` — true of
 * nearly every stored quote — so a backfill inside its branches would reach
 * only the handful that still predate sections, every current quote would
 * arrive with fulfilment undefined, every chip would read not-done, and every
 * won quote would sit in Won. It would look like the feature simply did not
 * work, with no error anywhere. The terms seeding already made this mistake
 * once (see the comment above migrateQuoteShape in MspOsApp.jsx).
 */
export function withFulfilment(quote) {
  if (!quote) return quote;
  if (hasAllSteps(quote.fulfilment)) return quote;
  return { ...quote, fulfilment: { ...emptyFulfilment(), ...(quote.fulfilment || {}) } };
}

/**
 * The current fulfilment, all four keys present, whatever the record holds.
 * ⚠ Use THIS, never `quote.fulfilment` raw, before spreading: a quote not read
 * since the migration shipped may still lack the field, and `{ ...undefined,
 * autotask: now }` would silently drop the other three steps.
 */
export const fulfilmentOf = (quote) => ({ ...emptyFulfilment(), ...(quote?.fulfilment || {}) });

export const isStepDone = (quote, step) => !!quote?.fulfilment?.[step];

export const doneSteps = (quote) => FULFILMENT_STEPS.filter((s) => isStepDone(quote, s));

export const isFulfilled = (quote) => FULFILMENT_STEPS.every((s) => isStepDone(quote, s));

/** Won — the cards that show the fulfilment chips. */
export const showsFulfilment = (quote) => quote?.status === "won" || quote?.status === LEGACY_COMPLETED;

/**
 * Which board column a quote belongs in. null = not on the board.
 *
 * won + all four steps → complete; otherwise the FURTHEST step done in
 * FULFILMENT_STEPS order, REGARDLESS OF GAPS — Invoiced ✓ Paid ✗ Ordered ✓
 * Autotask ✗ sits in Ordered, not Invoiced. That is the whole point and the
 * thing a normal kanban gets wrong: the card shows every step, so the gaps
 * stay visible from wherever it sits. An unknown or missing status degrades to
 * Draft rather than throwing, as quoteStateView never returns undefined.
 */
export function columnFor(quote) {
  const status = quote?.status;
  if (status === "lost") return null;
  if (status === "sent") return "sent";
  if (status !== "won" && status !== LEGACY_COMPLETED) return "draft";
  const done = doneSteps(quote);
  if (done.length === FULFILMENT_STEPS.length) return "complete";
  return done.length ? done[done.length - 1] : "won";
}

/**
 * Toggle ONE step. On = stamp `now`, off = null. Nothing else in the record is
 * touched — ticking Ordered never backfills Invoiced or Paid, and unticking a
 * middle step leaves its neighbours alone. Returns the patch for updateQuote.
 */
export function toggleStep(quote, step, now = Date.now()) {
  if (!FULFILMENT_STEPS.includes(step)) throw new Error(`Unknown fulfilment step: ${step}`);
  const current = { ...emptyFulfilment(), ...(quote?.fulfilment || {}) };
  return { fulfilment: { ...current, [step]: current[step] ? null : now } };
}

/**
 * The updateQuote patch for a SUCCESSFUL Push to Autotask, or null when the
 * push did not produce a ticket — a failed push must not tick anything.
 *
 * The one automated tick, and the only one wired: Craig asked for the push to
 * tick its own step. invoiced / paid wait for QBO; ordered has no system
 * behind it and stays manual.
 *
 * ⚠ TWO FIELDS, TWO MEANINGS — keep both. `autotaskPushedAt` is what HAPPENED,
 * written by the push and never by Craig. `fulfilment.autotask` is whether
 * Craig CONSIDERS the step done: he can untick it, and a later successful
 * push ticks it again. Do not "simplify" the chip to read autotaskPushedAt —
 * that removes the untick and re-creates the app arguing with the person
 * using it, the very thing acceptanceReconciledAt in quoteAcceptance.js exists
 * to stop. An existing stamp is NOT overwritten: the first successful push is
 * when it happened, and a re-push (or a forced one) must not rewrite history.
 *
 * ⚠ A push BEFORE the quote is won stamps the flag INVISIBLY — chips only
 * render on won cards and columnFor only routes won quotes into fulfilment
 * columns — and it appears already ticked when the quote later becomes won.
 * That is the right outcome (the push genuinely happened), not a bug; do not
 * gate the stamp on status.
 */
export function autotaskPushPatch(quote, data, now = Date.now()) {
  if (!data?.autotaskTicketId) return null;
  const current = fulfilmentOf(quote);
  return {
    autotaskTicketId: data.autotaskTicketId,
    autotaskTicketNumber: data.autotaskTicketNumber || null,
    autotaskPushedAt: data.autotaskPushedAt || new Date(now).toISOString(),
    // The field the older display code reads. Same ticket.
    autotaskQuoteId: data.autotaskTicketId,
    fulfilment: { ...current, autotask: current.autotask || now },
  };
}

/** Set ONE step done (a drop onto its column). Already done → no change. */
export function setStepDone(quote, step, now = Date.now()) {
  if (!FULFILMENT_STEPS.includes(step)) throw new Error(`Unknown fulfilment step: ${step}`);
  const current = { ...emptyFulfilment(), ...(quote?.fulfilment || {}) };
  if (current[step]) return null;
  return { fulfilment: { ...current, [step]: now } };
}

/** The board: every column, in order, with its quotes. Off-board quotes are not returned. */
export function groupByColumn(quotes) {
  const cols = Object.fromEntries(BOARD_COLUMNS.map((c) => [c, []]));
  (quotes || []).forEach((q) => {
    const col = columnFor(q);
    if (col) cols[col].push(q);
  });
  return cols;
}
